import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { SplashScreen } from '../../components/SplashScreen';
import {
  PATH_MISSION_ACCOUNTING_DEFAULT,
  PATH_MISSION_ACCOUNTING_INIT,
  PATH_MISSION_DEFAULT,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Loading } from '../../components/Loading';
import { FillForms } from '../../components/Illustrations/FillForms';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useMissionContext } from '../../contexts/MissionContext/hooks/useMissionContext.hook';
import { useAccountingContext } from '../../contexts/AccountingContext/hooks/useAccountingContext.hook';
import { AccountingContextStatus } from '../../contexts/AccountingContext/types/accounting-context.type';
import { useMissionBreadcrumbPaths } from '../../hooks/useMissionBreadcrumbPaths.hook';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { MissionAccountingInit } from './Init';
import { MissionAccountingReady } from './Ready';

export const MissionAccountingPage = () => {
  const navigate = useNavigate();

  const { missionId, folderId, mission } = useMissionContext();
  const access = useMissionAccess(missionId, mission);
  const missionWording = useMissionSpaceWording();
  const missionBreadcrumbPaths = useMissionBreadcrumbPaths();
  const accountingContext = useAccountingContext();

  const { paths: missionPaths, currentPathKey } = useBuildPaths(
    {
      default: PATH_MISSION_DEFAULT,
      accounting: PATH_MISSION_ACCOUNTING_DEFAULT,
      initAccounting: PATH_MISSION_ACCOUNTING_INIT,
    },
    { folderId, missionId },
  );

  const goToInitPage = useCallback(
    () => void navigate(missionPaths.initAccounting),
    [navigate, missionPaths.initAccounting],
  );

  const isInitAccountingPath = currentPathKey === 'initAccounting';

  if (accountingContext.status === AccountingContextStatus.Ready) {
    return <MissionAccountingReady />;
  }

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            isInitAccountingPath
              ? 'Initialisation de la Comptabilité'
              : `Fiche Comptabilité ${missionWording.ofName}`,
            mission?.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          ...missionBreadcrumbPaths,
          {
            to: missionPaths.accounting,
            label: 'comptabilité',
            key: 'accounting',
          },
        ]}
      />
    </>
  );

  const titleContent = (
    <PageTitle
      code={mission?.code}
      title={
        isInitAccountingPath
          ? 'Initialisation de la Comptabilité'
          : `Fiche Comptabilité ${missionWording.ofName}`
      }
    />
  );

  if (accountingContext.status === AccountingContextStatus.NotInitialized) {
    return (
      <>
        {beforeTitleContent}
        {titleContent}
        {isInitAccountingPath ? (
          <MissionAccountingInit />
        ) : (
          <SplashScreen
            action={{
              label: 'Initialiser la Comptabilité',
              onClick: goToInitPage,
              gate: access.initAccounting,
            }}
            illustration={
              <FillForms title={`Comptabilité ${missionWording.ofName}`} />
            }
            message={`Vous n'avez pas encore initialisé la comptabilité ${missionWording.ofName}. Commencez le travail !`}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
